import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { format, formatDistanceStrict } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { resolveDisplayZone, formatInZone } from '../lib/timezone';
import {
    ChevronLeft, RefreshCw, Users, Activity, CheckCircle, XCircle, Clock,
    ChevronRight, ChevronDown, Search, X, Mail, GitBranch, Tag, Flag,
    Bell, CheckSquare, Sparkles, Zap, Globe, Target, Timer, FileText,
    Send, MessageCircle, MessageSquareQuote, Eye, MousePointerClick,
    Loader2, Pause, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Step type icon map ─────────────────────────────────────────────────────
const STEP_ICONS = {
    email: Mail, wait: Clock, if_else: GitBranch, branch: GitBranch,
    tag_update: Tag, end: Flag, notification: Bell, task: CheckSquare,
    agentic_email: Sparkles, ab_split: Zap, webhook_call: Globe,
    deal_stage_update: Target, delay_until: Timer, wait_for_event: Timer,
    update_field: FileText, assign_user: Users, add_to_workflow: Send,
    goal: Target, request_testimonial: MessageSquareQuote, send_whatsapp: MessageCircle,
    ai_branch: Sparkles,
};

// ─── Status badge config ────────────────────────────────────────────────────
const STATUS_CONFIG = {
    active: { label: 'Active', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    completed: { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
    failed: { label: 'Failed', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    paused: { label: 'Paused', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    manually_exited: { label: 'Exited', bg: 'bg-stone-100', text: 'text-stone-600', dot: 'bg-stone-400' },
    exited: { label: 'Exited', bg: 'bg-stone-100', text: 'text-stone-600', dot: 'bg-stone-400' },
};

function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
}

function CampaignStatusBadge({ status }) {
    const map = {
        draft: 'bg-stone-100 text-stone-600',
        active: 'bg-emerald-50 text-emerald-700',
        paused: 'bg-amber-50 text-amber-700',
        archived: 'bg-stone-100 text-stone-500',
    };
    return (
        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${map[status] || map.draft}`}>
            {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </span>
    );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color = 'stone' }) {
    const colors = {
        stone: 'bg-stone-50 text-stone-700',
        emerald: 'bg-emerald-50 text-emerald-700',
        amber: 'bg-amber-50 text-amber-700',
        blue: 'bg-blue-50 text-blue-700',
        red: 'bg-red-50 text-red-700',
    };
    return (
        <div className={`rounded-lg p-4 ${colors[color]}`}>
            <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-4 h-4 opacity-60" />
                <span className="text-[11px] font-medium uppercase tracking-wide opacity-60">{label}</span>
            </div>
            <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</div>
        </div>
    );
}

// ─── Timezone-aware date formatter ─────────────────────────────────────────
// F09 (Epic #532 audit): the direct date-fns-tz import was the last
// formatter bypass in a suite-touched surface. formatInZone wraps
// date-fns-tz and returns '' for invalid input, so the Pending/verbatim
// fallbacks are kept explicit here instead.
function formatTz(dateStr, tz, fmt) {
    if (!dateStr) return 'Pending';
    return formatInZone(dateStr, { zone: tz || 'UTC', formatStr: fmt || 'MMM d, yyyy h:mm a zzz' })
        || fallbackFormat(dateStr);
}

function fallbackFormat(dateStr) {
    try { return format(new Date(dateStr), 'MMM d, yyyy h:mm a'); } catch { return dateStr; }
}

// ─── Email Preview Modal ───────────────────────────────────────────────────
function EmailPreviewModal({ data, loading, onClose, tz }) {
    if (!data && !loading) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-stone-500" />
                        <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Email Preview
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                    </div>
                ) : data ? (
                    <>
                        {/* Email metadata */}
                        <div className="px-5 py-3 border-b border-stone-100 space-y-1.5 flex-shrink-0 bg-stone-50/50">
                            <div className="flex items-start gap-2">
                                <span className="text-[11px] font-medium text-stone-400 w-12 text-right mt-0.5 flex-shrink-0">From</span>
                                <span className="text-xs text-stone-700">{data.email_from || '—'}</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-[11px] font-medium text-stone-400 w-12 text-right mt-0.5 flex-shrink-0">To</span>
                                <span className="text-xs text-stone-700">{data.email_to || '—'}</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-[11px] font-medium text-stone-400 w-12 text-right mt-0.5 flex-shrink-0">Subject</span>
                                <span className="text-xs font-medium text-stone-900">{data.email_subject || '(No subject)'}</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-[11px] font-medium text-stone-400 w-12 text-right mt-0.5 flex-shrink-0">Sent</span>
                                <span className="text-xs text-stone-600">{data.sent_at ? formatTz(data.sent_at, tz) : '—'}</span>
                            </div>
                            {/* Engagement stats */}
                            <div className="flex items-center gap-4 pt-1">
                                <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                    data.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' :
                                    data.status === 'bounced' ? 'bg-red-50 text-red-600' :
                                    'bg-blue-50 text-blue-600'
                                }`}>
                                    {data.status}
                                </span>
                                {data.opens > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-blue-600">
                                        <Eye className="w-3 h-3" />
                                        {data.opens} open{data.opens !== 1 ? 's' : ''}
                                        {data.first_opened_at && <span className="text-stone-400 ml-1">({formatTz(data.first_opened_at, tz, 'MMM d, h:mm a')})</span>}
                                    </span>
                                )}
                                {data.clicks > 0 && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-indigo-600">
                                        <MousePointerClick className="w-3 h-3" />
                                        {data.clicks} click{data.clicks !== 1 ? 's' : ''}
                                        {data.first_clicked_at && <span className="text-stone-400 ml-1">({formatTz(data.first_clicked_at, tz, 'MMM d, h:mm a')})</span>}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Email body */}
                        <div className="flex-1 overflow-auto p-1" style={{ backgroundColor: '#f5f5f4' }}>
                            {data.email_html ? (
                                <iframe
                                    title="Email preview"
                                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;font-family:Arial,sans-serif;background:#fff;}</style></head><body>${data.email_html}</body></html>`}
                                    className="w-full border-0 rounded bg-white"
                                    style={{ minHeight: 400 }}
                                    onLoad={e => {
                                        try {
                                            const doc = e.target.contentDocument;
                                            if (doc) e.target.style.height = Math.max(400, doc.body.scrollHeight + 40) + 'px';
                                        } catch {}
                                    }}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center py-16 text-stone-400">
                                    <Mail className="w-8 h-8 mb-2 opacity-50" />
                                    <p className="text-xs">Email content not available for preview.</p>
                                    <p className="text-[10px] mt-1">Only emails sent after this update include preview content.</p>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-stone-400">
                        <Mail className="w-8 h-8 mb-2 opacity-50" />
                        <p className="text-xs">No email data found.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Step Timeline in Expanded Row ──────────────────────────────────────────
function StepTimeline({ steps, tz, onPreviewEmail }) {
    if (!steps || steps.length === 0) {
        return <p className="text-xs text-stone-400 py-2">No step history available.</p>;
    }

    return (
        <div className="relative pl-6 space-y-0">
            {steps.map((step, i) => {
                const Icon = STEP_ICONS[step.step_type] || Clock;
                const isLast = i === steps.length - 1;
                const prevTime = i > 0 && steps[i - 1].entered_at ? new Date(steps[i - 1].entered_at) : null;
                const curTime = step.entered_at ? new Date(step.entered_at) : null;
                const duration = prevTime && curTime
                    ? formatDistanceStrict(prevTime, curTime)
                    : null;

                const outcomeColor =
                    step.outcome === 'sent' || step.outcome === 'completed' || step.outcome === 'branched'
                        ? 'text-emerald-600 bg-emerald-50'
                        : step.outcome === 'failed'
                            ? 'text-red-600 bg-red-50'
                            : step.outcome === 'waiting' || step.outcome === 'timed_out'
                                ? 'text-amber-600 bg-amber-50'
                                : 'text-stone-500 bg-stone-100';

                const isEmailStep = step.step_type === 'email' || step.step_type === 'agentic_email';

                return (
                    <div key={i} className="relative pb-4">
                        {/* Vertical line */}
                        {!isLast && (
                            <div className="absolute left-[-16px] top-6 bottom-0 w-px bg-stone-200" />
                        )}
                        {/* Icon dot */}
                        <div className="absolute left-[-22px] top-1 w-5 h-5 rounded-full bg-white border border-stone-200 flex items-center justify-center">
                            <Icon className="w-3 h-3 text-stone-500" />
                        </div>
                        {/* Content */}
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {step.step_label || step.step_type}
                                </span>
                                {step.outcome && (
                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${outcomeColor}`}>
                                        {step.outcome}
                                    </span>
                                )}
                                {duration && (
                                    <span className="text-[10px] text-stone-400">
                                        +{duration}
                                    </span>
                                )}
                                {/* View Email button for email steps */}
                                {isEmailStep && step.outcome === 'sent' && onPreviewEmail && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onPreviewEmail(step.step_id); }}
                                        className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-full transition-colors font-medium"
                                    >
                                        <ExternalLink className="w-2.5 h-2.5" />
                                        View Email
                                    </button>
                                )}
                            </div>
                            <div className="text-xs text-stone-400 mt-0.5">
                                {formatTz(step.entered_at, tz)}
                            </div>
                            {/* Email engagement indicators */}
                            {isEmailStep && (step.opened_at || step.clicked_at || step.email_opens > 0 || step.email_clicks > 0) && (
                                <div className="flex items-center gap-3 mt-1">
                                    {(step.opened_at || step.email_opens > 0) && (
                                        <span className="inline-flex items-center gap-1 text-[10px] text-blue-600">
                                            <Eye className="w-3 h-3" />
                                            {step.opened_at
                                                ? <>Opened {formatTz(step.opened_at, tz, 'MMM d, h:mm a zzz')}</>
                                                : <>{step.email_opens} open{step.email_opens !== 1 ? 's' : ''}</>
                                            }
                                        </span>
                                    )}
                                    {(step.clicked_at || step.email_clicks > 0) && (
                                        <span className="inline-flex items-center gap-1 text-[10px] text-indigo-600">
                                            <MousePointerClick className="w-3 h-3" />
                                            {step.clicked_at
                                                ? <>Clicked {formatTz(step.clicked_at, tz, 'MMM d, h:mm a zzz')}</>
                                                : <>{step.email_clicks} click{step.email_clicks !== 1 ? 's' : ''}</>
                                            }
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Main Page ──────────────────────────────────────────────────────────────
export default function CampaignExecutionReport() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const token = localStorage.getItem('crm_token');
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // Campaign data
    const [campaign, setCampaign] = useState(null);
    const [campaignLoading, setCampaignLoading] = useState(true);

    // Report data
    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(true);

    // Filters
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [stepFilter, setStepFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Expanded rows
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Refresh counter
    const [refreshKey, setRefreshKey] = useState(0);

    // Debounce search
    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);
        return () => clearTimeout(debounceRef.current);
    }, [search]);

    // Fetch campaign
    useEffect(() => {
        setCampaignLoading(true);
        axios.get(`${API}/campaigns/${campaignId}`, { headers })
            .then(res => setCampaign(res.data))
            .catch(console.error)
            .finally(() => setCampaignLoading(false));
    }, [campaignId, headers, refreshKey]);

    // Fetch execution report
    useEffect(() => {
        setReportLoading(true);
        const params = new URLSearchParams();
        params.set('page', page);
        params.set('page_size', pageSize);
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (statusFilter) params.set('status', statusFilter);
        if (stepFilter) params.set('step_id', stepFilter);
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);

        axios.get(`${API}/campaigns/${campaignId}/execution-report?${params.toString()}`, { headers })
            .then(res => setReport(res.data))
            .catch(console.error)
            .finally(() => setReportLoading(false));
    }, [campaignId, headers, page, pageSize, debouncedSearch, statusFilter, stepFilter, dateFrom, dateTo, refreshKey]);

    const toggleRow = useCallback((enrollmentId) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(enrollmentId)) next.delete(enrollmentId);
            else next.add(enrollmentId);
            return next;
        });
    }, []);

    const clearFilters = useCallback(() => {
        setSearch('');
        setDebouncedSearch('');
        setStatusFilter('');
        setStepFilter('');
        setDateFrom('');
        setDateTo('');
        setPage(1);
    }, []);

    const hasFilters = search || statusFilter || stepFilter || dateFrom || dateTo;

    const summary = report?.summary || {};
    const enrollments = report?.data || [];
    const total = report?.total || 0;
    const totalPages = Math.ceil(total / pageSize);
    // #541: report stamps render in the active VIEWER zone (profile → browser
    // → UTC); executionTimezone stays the label for where the campaign RAN.
    const viewerTimezone = resolveDisplayZone({ user });
    const executionTimezone = report?.execution_timezone || 'UTC';

    // Email preview modal state
    const [emailPreview, setEmailPreview] = useState(null);
    const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);

    const handlePreviewEmail = useCallback((enrollmentId, stepId) => {
        setEmailPreviewLoading(true);
        setEmailPreview(null);
        axios.get(`${API}/campaigns/${campaignId}/email-preview/${enrollmentId}/${stepId}`, { headers })
            .then(res => setEmailPreview(res.data))
            .catch(err => {
                console.error('Failed to load email preview:', err);
                setEmailPreview({ error: true });
            })
            .finally(() => setEmailPreviewLoading(false));
    }, [campaignId, headers]);

    // Build step options from campaign
    const stepOptions = useMemo(() => {
        if (!campaign?.steps) return [];
        return campaign.steps.map(s => ({
            id: s.id,
            label: s.data?.label || s.type || s.id,
        }));
    }, [campaign]);

    const startIdx = (page - 1) * pageSize + 1;
    const endIdx = Math.min(page * pageSize, total);

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
            {/* Header */}
            <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
                    <button
                        onClick={() => navigate(`/campaigns/${campaignId}/edit`)}
                        className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
                    >
                        <ChevronLeft className="w-5 h-5 text-stone-600" />
                    </button>
                    <div className="flex-1 min-w-0">
                        {campaignLoading ? (
                            <div className="h-6 w-48 bg-stone-100 rounded animate-pulse" />
                        ) : (
                            <div className="flex items-center gap-3">
                                <h1 className="text-lg font-bold text-stone-800 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {campaign?.name || 'Campaign'} — Execution Report
                                </h1>
                                {campaign?.status && <CampaignStatusBadge status={campaign.status} />}
                            </div>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRefreshKey(k => k + 1)}
                        className="gap-1.5"
                    >
                        <RefreshCw className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <StatCard icon={Users} label="Total Enrolled" value={summary.total_enrolled ?? '—'} color="stone" />
                    <StatCard icon={Activity} label="Active" value={summary.active ?? '—'} color="blue" />
                    <StatCard icon={CheckCircle} label="Completed" value={summary.completed ?? '—'} color="emerald" />
                    <StatCard icon={XCircle} label="Failed" value={summary.failed ?? '—'} color="red" />
                    <StatCard icon={Clock} label="Avg Completion (hrs)" value={summary.avg_completion_hours != null ? summary.avg_completion_hours.toFixed(1) : '—'} color="amber" />
                </div>

                {/* Filter Bar */}
                <div className="bg-white rounded-lg border border-stone-200 p-3">
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Search */}
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                            <input
                                type="text"
                                placeholder="Search name or email..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="w-full pl-8 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-300 bg-white"
                            />
                        </div>

                        {/* Status */}
                        <select
                            value={statusFilter}
                            onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                            className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-300"
                        >
                            <option value="">All Statuses</option>
                            <option value="active">Active</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="paused">Paused</option>
                            <option value="manually_exited">Exited</option>
                        </select>

                        {/* Current Step */}
                        <select
                            value={stepFilter}
                            onChange={e => { setStepFilter(e.target.value); setPage(1); }}
                            className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-300"
                        >
                            <option value="">All Steps</option>
                            {stepOptions.map(s => (
                                <option key={s.id} value={s.id}>{s.label}</option>
                            ))}
                        </select>

                        {/* Date From */}
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                            className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-300"
                            placeholder="From"
                        />

                        {/* Date To */}
                        <input
                            type="date"
                            value={dateTo}
                            onChange={e => { setDateTo(e.target.value); setPage(1); }}
                            className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-300"
                            placeholder="To"
                        />

                        {/* Clear */}
                        {hasFilters && (
                            <button
                                onClick={clearFilters}
                                className="inline-flex items-center gap-1 px-3 py-2 text-sm text-stone-500 hover:text-stone-700 hover:bg-stone-50 rounded-lg transition-colors"
                            >
                                <X className="w-3.5 h-3.5" />
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Contact Table */}
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    {reportLoading && !report ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                        </div>
                    ) : enrollments.length === 0 ? (
                        <div className="text-center py-16">
                            <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-sm text-stone-500">
                                {hasFilters ? 'No enrollments match your filters.' : 'No enrollments yet.'}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Table Header */}
                            <div className="grid grid-cols-[1fr_100px_120px_110px_80px_120px_40px] gap-2 px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-[11px] font-semibold text-stone-500 uppercase tracking-wider">
                                <span>Contact</span>
                                <span>Status</span>
                                <span>Current Step</span>
                                <span>Enrolled</span>
                                <span>Steps</span>
                                <span>Emails</span>
                                <span />
                            </div>

                            {/* Table Rows */}
                            {enrollments.map(enrollment => {
                                const isExpanded = expandedRows.has(enrollment.enrollment_id);
                                const stepsCompleted = enrollment.steps_completed ?? 0;
                                const totalSteps = enrollment.total_steps ?? 0;
                                const emailsSent = enrollment.emails_sent ?? 0;
                                const emailsOpened = enrollment.emails_opened ?? 0;
                                const emailsClicked = enrollment.emails_clicked ?? 0;

                                return (
                                    <div key={enrollment.enrollment_id} className="border-b border-stone-100 last:border-b-0">
                                        <button
                                            onClick={() => toggleRow(enrollment.enrollment_id)}
                                            className="w-full grid grid-cols-[1fr_100px_120px_110px_80px_120px_40px] gap-2 px-4 py-3 items-center hover:bg-stone-50/50 transition-colors text-left"
                                        >
                                            {/* Contact */}
                                            <div className="min-w-0">
                                                <Link
                                                    to={`/contacts/${enrollment.contact_id}`}
                                                    onClick={e => e.stopPropagation()}
                                                    className="text-sm font-medium text-stone-800 hover:text-blue-600 truncate block"
                                                >
                                                    {enrollment.contact_name || (enrollment.contact_email ? enrollment.contact_email.split('@')[0] : 'Unknown')}
                                                </Link>
                                                <span className="text-xs text-stone-400 truncate block">{enrollment.contact_email}</span>
                                            </div>

                                            {/* Status */}
                                            <div>
                                                <StatusBadge status={enrollment.status} />
                                            </div>

                                            {/* Current Step */}
                                            <div className="text-xs text-stone-600 truncate">
                                                {enrollment.current_step_label || '—'}
                                            </div>

                                            {/* Enrolled */}
                                            <div className="text-xs text-stone-500">
                                                {enrollment.enrolled_at
                                                    ? formatTz(enrollment.enrolled_at, viewerTimezone, 'MMM d, yyyy')
                                                    : '—'}
                                            </div>

                                            {/* Steps */}
                                            <div className="text-xs text-stone-600 font-medium">
                                                {stepsCompleted}/{totalSteps}
                                            </div>

                                            {/* Emails */}
                                            <div className="flex items-center gap-2 text-xs text-stone-500">
                                                <span className="inline-flex items-center gap-0.5" title="Sent">
                                                    <Mail className="w-3 h-3" />{emailsSent}
                                                </span>
                                                <span className="inline-flex items-center gap-0.5 text-blue-500" title="Opened">
                                                    <Eye className="w-3 h-3" />{emailsOpened}
                                                </span>
                                                <span className="inline-flex items-center gap-0.5 text-indigo-500" title="Clicked">
                                                    <MousePointerClick className="w-3 h-3" />{emailsClicked}
                                                </span>
                                            </div>

                                            {/* Expand Arrow */}
                                            <div className="flex justify-center">
                                                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                            </div>
                                        </button>

                                        {/* Expanded Detail */}
                                        {isExpanded && (
                                            <div className="px-6 py-4 bg-stone-50/60 border-t border-stone-100">
                                                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                                    Step Journey
                                                </h4>
                                                <StepTimeline
                                                    steps={enrollment.step_history || []}
                                                    tz={executionTimezone}
                                                    onPreviewEmail={(stepId) => handlePreviewEmail(enrollment.enrollment_id, stepId)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>

                {/* Pagination */}
                {total > 0 && (
                    <div className="flex items-center justify-between bg-white rounded-lg border border-stone-200 px-4 py-3">
                        <span className="text-sm text-stone-500">
                            Showing {startIdx}-{endIdx} of {total}
                        </span>
                        <div className="flex items-center gap-3">
                            <select
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                                className="px-2 py-1 text-sm border border-stone-200 rounded bg-white focus:outline-none"
                            >
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => setPage(p => p - 1)}
                            >
                                Prev
                            </Button>
                            <span className="text-sm text-stone-500">
                                Page {page} of {totalPages || 1}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Email Preview Modal */}
            {(emailPreview || emailPreviewLoading) && (
                <EmailPreviewModal
                    data={emailPreview}
                    loading={emailPreviewLoading}
                    onClose={() => { setEmailPreview(null); setEmailPreviewLoading(false); }}
                    tz={executionTimezone}
                />
            )}
        </div>
    );
}
